import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { faArrowRotateForward, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faEnvelopes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { paginationModel, usePagination } from "api/hooks/usePagination";
import { useGetTherapistInvitations, usePutResendTherapistInvitation } from "api/services/therapist/hooks";
import { ButtonContainer } from "routes/admin-billing/components/SharedStyles";
import { DateFilter } from "routes/invitations/components/DateFilter";
import { useDateFilter } from "routes/invitations/components/useDateFilter";
import { Spinner } from "shared/atoms/Spinner";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { CheckIcon, XmarkIcon } from "shared/icons";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { useLocalizedDate } from "utils/date";

type Status = "pending" | "success" | "error";

export type ResendState = {
  id: number;
  status: Status;
};

export const StatusIcon: React.FC<{ status: Status }> = ({ status }) => {
  switch (status) {
    case "pending":
      return <Spinner style={{ height: "40px", width: "40px" }} />;
    case "success":
      return <CheckIcon />;
    case "error":
      return <XmarkIcon />;
    default:
      return null;
  }
};

export const InvitationTable: React.FC = () => {
  const { t } = useTranslation();
  const { format, formats } = useLocalizedDate();
  const { pagination, setPagination } = usePagination();
  const [resendState, setResendState] = useState<ResendState | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data, isLoading, refetch } = useGetTherapistInvitations({
    params: {
      limit: pagination.limit,
      page: pagination.page + 1,
    },
  });

  const putResendTherapistInvitation = usePutResendTherapistInvitation();

  const allRows = data?.items || [];
  const metadata = data?.metadata;
  const [rowCountState, setRowCountState] = useState(metadata?.total_count);

  const { dateFilter, handleFilterChange, filteredRows: rows } = useDateFilter(allRows);

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      metadata?.total_count !== undefined ? metadata.total_count : prevRowCountState
    );
  }, [metadata, setRowCountState]);

  const onResendInvitation = async (id: number) => {
    setResendState({ id, status: "pending" });
    try {
      await putResendTherapistInvitation.mutateAsync(id, {
        onError: () => {
          setResendState({ id, status: "error" });
          refetch();
        },
        onSuccess: () => {
          setResendState({ id, status: "success" });
          refetch();
        },
        onSettled: () => {
          setTimeout(() => {
            setResendState(null);
          }, 2000);
        },
      });
    } catch (error) {
      setResendState({ id, status: "error" });
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: t("invitations.table.name"),
        flex: 1,
        minWidth: 155,
        maxWidth: 300,
      },
      {
        field: "sent_at",
        headerName: t("invitations.table.sent_at"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), formats.LONG_LOCALIZED_DATE_PP) : ""),
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
      },
      {
        field: "last_reminder_at",
        headerName: t("invitations.table.last_reminder_at"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), formats.LONG_LOCALIZED_DATE_PP) : ""),
        flex: 1,
        minWidth: 188,
        maxWidth: 300,
      },
      {
        field: "reminder_count",
        headerName: t("invitations.table.reminder_count"),
        disableColumnMenu: true,
        flex: 1,
        minWidth: 135,
        maxWidth: 300,
      },
      {
        field: "_resend_invitation",
        headerName: t("invitations.table.resend_invitation"),
        renderCell: value => (
          <ButtonContainer
            onClick={() => {
              onResendInvitation(value.row.id);
            }}
            style={{ justifyContent: "start" }}
          >
            {resendState?.id === value.row.id && resendState?.status ? (
              <StatusIcon status={resendState.status} />
            ) : (
              <FontAwesomeIcon icon={faArrowRotateForward} style={{ height: "18px", width: "18px" }} />
            )}
          </ButtonContainer>
        ),
        disableColumnMenu: true,
        sortable: false,
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
      },
    ];
  }, [resendState]);

  return (
    <WidgetBox
      icon={<FontAwesomeIcon icon={faEnvelopes} />}
      title={t("invitations.table.title")}
      description={t("invitations.table.subheading")}
      color="blue"
      notifications={metadata?.total_count}
      toolbar={<DateFilter value={dateFilter} onChange={handleFilterChange} />}
    >
      <MUIDataGrid
        columns={columns}
        rows={rows}
        rowHeight={46}
        getRowId={row => row.id}
        loading={isLoading}
        initialState={{ pagination: { paginationModel } }}
        paginationMode="server"
        onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
        rowCount={rowCountState}
        rowSelection={false}
        slots={{
          noRowsOverlay: () => <EmptyResultOverlay text={t("invitations.table.no_rows")} icon={faUser} />,
          noResultsOverlay: () => <EmptyResultOverlay />,
        }}
        localeText={{
          noRowsLabel: "",
          noResultsOverlayLabel: "",
        }}
        filterModel={filterModel}
        onFilterModelChange={model => setFilterModel(model)}
        disableColumnResize
        transparentBackground
        sx={{ marginTop: "-16px" }}
      />
    </WidgetBox>
  );
};
